import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { tabSync } from "@/utils/tabSync";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { checkUserSubscription } from "@/utils/subscription";

interface PrivateRouteProps {
  children: React.ReactNode;
}

export const PrivateRoute = ({ children }: PrivateRouteProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const authStateLogRef = useRef<number>(0);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Attempt to get the session using secure cookies
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("[PrivateRoute] Error checking auth status:", error);
          throw error;
        }

        if (!session || !session.user) {
          console.log("[PrivateRoute] No valid session found, redirecting to auth");
          navigate("/auth?view=sign_in", {
            state: { from: location.pathname }
          });
          return;
        }
        
        // Check subscription status on login
        try {
          console.log("[PrivateRoute] Verifying subscription status on login");
          await checkUserSubscription(session);
        } catch (error) {
          console.error("[PrivateRoute] Subscription check error:", error);
          // Continue anyway - this is a background check
        }

        // Perform onboarding check
        try {
          if (!session.user.id) {
            console.error("[PrivateRoute] Missing user ID in session");
            throw new Error("Invalid session state");
          }
          
          console.log("[PrivateRoute] Checking onboarding status for user", session.user.id);
          
          // Use the RPC function to check onboarding status
          const { data: onboardingComplete, error: onboardingError } = await supabase
            .rpc('check_onboarding_status', {
              user_id: session.user.id
            });

          if (onboardingError) {
            console.error("[PrivateRoute] Error checking onboarding status:", onboardingError);
            // Don't throw - we'll continue authentication anyway
          } else if (!onboardingComplete && location.pathname !== '/onboarding') {
            // Only redirect if we're not already on the onboarding page
            navigate("/onboarding");
            return;
          } else if (onboardingComplete) {
            console.log("[PrivateRoute] User has completed onboarding");
          }
        } catch (err) {
          console.error("[PrivateRoute] Error in onboarding check:", err);
          // Continue with authentication to prevent login loops
        }

        // If we got here, the user is authenticated
        setIsAuthenticated(true);

        // Handle email verification success
        const isEmailVerification = new URLSearchParams(location.search).get('type') === 'email_confirmation';
        if (isEmailVerification) {
          toast({
            title: "Email verified successfully!",
            description: "Welcome to Strykr.ai",
          });
          // Remove query parameters
          window.history.replaceState({}, '', location.pathname);
        }
      } catch (error) {
        console.error("[PrivateRoute] Authentication error:", error);
        
        // Provide better error context for CORS issues
        let errorDescription = "Please sign in to continue.";
        
        if (error instanceof Error && 
            (error.message.includes("cors") || 
             error.message.includes("CORS") || 
             error.message.includes("header"))) {
          errorDescription = "Cross-origin authentication error. Please try again.";
          console.error("[PrivateRoute] Possible CORS issue with secure cookies");
        }
        
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: errorDescription,
        });
        
        navigate("/auth?view=sign_in");
      } finally {
        setIsLoading(false);
      }
    };

    // Initial auth check
    checkAuth();

    // Listen for tab sync events (e.g., onboarding completion)
    const unsubscribe = tabSync.subscribe(() => {
      console.log('[PrivateRoute] Received tab sync notification, rechecking auth...');
      checkAuth();
    });

    // Set up auth state change listener
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      // Throttle auth state change logging to prevent spam
      const now = Date.now();
      const lastLogTime = authStateLogRef.current;
      if (now - lastLogTime > 30000) { // Only log every 30 seconds instead of 5
        console.log("[PrivateRoute] Auth state change:", event);
        authStateLogRef.current = now;
      }
      
      if (event === "SIGNED_OUT" || !session) {
        setIsAuthenticated(false);
        navigate("/auth?view=sign_in");
      } else if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
        // Perform a full auth check when we get a new session
        checkAuth();
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
      unsubscribe();
    };
  }, [navigate, toast, location]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : null;
};